import { Dropdown } from "antd"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import Geocode from "react-geocode"
import { FiFilter } from "react-icons/fi"
import styled from "styled-components"

import { useStateValue } from "../../../context/AppStateStore"
import usePaginatedRestResource from "../../../hooks/usePaginatedRestResource"
import StyledAppButton from "../../AppButton"
import FormInput from "../FormInput"
import FormSelect from "../FormSelect"

// ----------------------------------------------------------------------------

function _RoomsFilter(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className, onFilter } = props

    // -------------------------------------
    // Hooks (e.g. useState, ...)
    // -------------------------------------

    const [{ token }] = useStateValue()

    const [users, , , , , handleFiltersChange] = usePaginatedRestResource(
        "/users",
        token,
        {
            filters: { role: "agency" },
            pageSize: 50,
        }
    )

    const [filters, setFilters] = useState({ address: "", managers: [] })
    const [visible, setVisible] = useState(false)
    const [userSearch, setUserSearch] = useState("")
    const [loading, setLoading] = useState()

    // -------------------------------------
    // Memoized values
    // -------------------------------------

    const userOptions = useMemo(() => {
        return users?.map((user) => ({
            label: user.businessName,
            value: user.id,
        }))
    }, [users])

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        if (userSearch && userSearch.length >= 3) {
            handleFiltersChange({ role: "agency", search: userSearch })
        } else {
            handleFiltersChange({ role: "agency", search: null })
        }
        // eslint-disable-next-line
    }, [userSearch])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    async function getAddressCoordinates() {
        setLoading(true)
        const { lat, lng } =
            (filters.address && (await Geocode.fromAddress(filters.address)))
                ?.results?.[0]?.geometry?.location ?? {}
        setLoading(false)
        return { lat, lng }
    }

    async function handleConfirm() {
        const { lat, lng } = await getAddressCoordinates()
        onFilter &&
            onFilter({
                ...filters,
                managers: filters.managers.join(","),
                ...(lat && lng && { lat, lng, max_distance: 50000 }),
            })
        setVisible(false)
    }

    function updateFilters(field) {
        return (value) => setFilters({ ...filters, [field]: value })
    }

    function renderDropdown() {
        return (
            <div className="filter-dropdown">
                <FormInput
                    label="Indirizzo"
                    value={filters.address}
                    onChange={updateFilters("address")}
                    className="field"
                    paddingHorizontal={10}
                />
                <FormSelect
                    label="Agenzia gestore"
                    multiple
                    value={filters.managers}
                    onChange={updateFilters("managers")}
                    className="field"
                    options={userOptions}
                    searchValue={userSearch}
                    onSearch={setUserSearch}
                    paddingHorizontal={10}
                />
                <StyledAppButton
                    uxType="primary"
                    className="confirm-btn"
                    onClick={handleConfirm}
                    loading={loading}
                >
                    Applica filtri
                </StyledAppButton>
            </div>
        )
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={`${className}`}>
            <Dropdown
                getPopupContainer={(node) => node.parentElement}
                overlay={renderDropdown}
                trigger="click"
                // don't close dropdown when item is clicked
                visible={visible}
                onVisibleChange={(newState) => setVisible(newState)}
            >
                <div className="filter-selector">
                    <div className="filter-icon">
                        <FiFilter />
                    </div>
                    <div className="filter-label">Ricerca stanza</div>
                </div>
            </Dropdown>
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_RoomsFilter.propTypes = {
    className: PropTypes.string.isRequired,
    onFilter: PropTypes.func,
}

_RoomsFilter.defaultProps = {}

// ----------------------------------------------------------------------------

const RoomsFilter = styled(_RoomsFilter)`
    & {
        margin-right: 20px;
        .filter-selector {
            height: 48px;
            width: 250px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0px 15px;
            border-radius: 6px;
            border: 1px solid black;
            cursor: pointer;

            .filter-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                svg {
                    width: 20px;
                    height: 20px;
                }
            }
            .filter-label {
                font-size: 16px;
                font-weight: 700;
                margin-left: 10px;
            }
        }
        .filter-dropdown {
            width: 100%;
            background-color: white;
            box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.2);
            padding: 10px;
            border-radius: 10px;

            display: flex;
            align-items: center;
            flex-direction: column;

            .field {
                width: 100%;
                margin-bottom: 15px;
            }

            .confirm-btn {
                margin-top: 10px;
                width: 100%;
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default RoomsFilter
